import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import api from '../../api/api';
import { useLanguage } from '../../context/LanguageContext';
import '../../assets/style/Security.css';

const PAGE_SIZE = 5;

export default function LoginHistory() {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (pageToLoad = 1) => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/logs', {
        params: {
          page: pageToLoad,
          per_page: PAGE_SIZE,
        },
      });

      const payload = response.data || {};
      const items = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      setLogs((current) => pageToLoad === 1 ? items : [...current, ...items]);
      setPage(pageToLoad);
      setHasMore(Boolean(payload.next_page_url) || payload.current_page < payload.last_page);
    } catch (requestError) {
      setError(requestError.response?.data?.message || t('networkError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const formatDate = (value) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  return (
    <Paper elevation={0} className="card security-card login-history">
      <div className="login-history__title">
        <Typography variant="h6" className="card-title">{t('loginHistory')}</Typography>
        <Typography variant="body2" className="card-subtitle">{t('recentLoginActivity')}</Typography>
      </div>

      <div className="history-table">
        <div className="table-header">
          <Typography className="table-header-cell">{t('loginType')}</Typography>
          <Typography className="table-header-cell">{t('ipAddress')}</Typography>
          <Typography className="table-header-cell">{t('client')}</Typography>
        </div>

        {logs.map((entry) => (
          <div className="table-row" key={entry.id}>
            <div className="table-cell">
              <Typography className="cell-main-text">{entry.login_type || 'Authentication'}</Typography>
              <Typography className="cell-sub-text">{formatDate(entry.created_at)}</Typography>
            </div>
            <div className="table-cell table-cell--ip">
              <Typography className="cell-main-text">{entry.ip || '-'}</Typography>
              {entry.is_new_ip && (
                <span className="login-history__badge">{t('newIpLogin')}</span>
              )}
            </div>
            <div className="table-cell">
              <Typography className="cell-main-text client-text">{entry.ua || '-'}</Typography>
            </div>
          </div>
        ))}

        {!loading && logs.length === 0 && (
          <Box className="login-history__empty">
            <Typography variant="body2">{error || t('noLoginHistory')}</Typography>
          </Box>
        )}

        {loading && logs.length === 0 && (
          <Box className="login-history__empty">
            <CircularProgress size={22} />
          </Box>
        )}
      </div>

      {error && logs.length > 0 && (
        <Typography className="login-history__error" variant="body2">
          {error}
        </Typography>
      )}

      {hasMore && (
        <Box className="login-history__footer">
          <Button
            className="login-history__show-more"
            onClick={() => fetchLogs(page + 1)}
            disabled={loading}
          >
            {loading ? t('loading') : t('showMore')}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
